import React, { useEffect, useState, useCallback, useRef } from "react";
import { Megaphone, RefreshCw, Plus, Trash2, Upload, X, ImageIcon, Pencil, Pause, Play, Search, MoreHorizontal, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { AdminShell } from "@/components/AdminSidebar";
import { FeatureAdPreview } from "@/components/FeatureAdPreview";
import { useTakeoverStatus, TakeoverBadge, TakeoverSummary } from "@/components/TakeoverBadge";
import { TakeoverRegenPanel, useTakeoverJob } from "@/components/admin/TakeoverRegen";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const PLACEMENTS = ["calendar", "registration", "pregame", "in_race", "post_race", "countdown_feature"];
const BE = process.env.REACT_APP_BACKEND_URL;

// Standard sponsor banner formats (all 324px wide). One creative per size.
const BANNER_SIZES = [
  { key: "single", label: "Banner X1", w: 324, h: 77, note: "thin strip · in-race" },
  { key: "double", label: "Banner X2", w: 324, h: 132, note: "calendar · registration · finish" },
  { key: "triple", label: "Banner X3", w: 324, h: 209, note: "pre-race lobby" },
];
const FEATURE_SIZE = { key: "feature", label: "Countdown Takeover", w: 1080, h: 2340, note: "full screen · final 10s · display-only" };
const GEN_SIZES = [...BANNER_SIZES, FEATURE_SIZE];
const srcFor = (u) => (u ? (u.startsWith("http") ? u : `${BE}${u}`) : null);
const SIZE_PLACEMENTS = {
  single: ["In-race"],
  double: ["Calendar", "Registration", "Finish-line"],
  triple: ["Pre-race lobby"],
};

// One prompt (+ optional reference) -> a consistent X1/X2/X3 banner set.
function GenAllSizes({ defaultPrompt = "", onResult }) {
  const refInput = useRef(null);
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [refImg, setRefImg] = useState("");
  const [refBusy, setRefBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [results, setResults] = useState({});   // {sizeKey: url} for the last generated set
  const [genMeta, setGenMeta] = useState(null);  // {prompt, seed} used, so a single size can be redone consistently
  const [regenKey, setRegenKey] = useState("");  // which size is currently regenerating
  const [lightbox, setLightbox] = useState("");  // enlarged preview url

  const doRefUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image."); return; }
    setRefBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRefImg(data.url);
      toast.success("Reference image added");
    } catch { toast.error("Reference upload failed."); }
    finally { setRefBusy(false); if (refInput.current) refInput.current.value = ""; }
  };

  const run = async () => {
    if (!prompt.trim()) { toast.error("Describe the ad first"); return; }
    setBusy(true);
    try {
      let seed = refImg;                 // uploaded reference, or empty
      const promptUsed = prompt.trim();
      const collected = {};
      for (const s of GEN_SIZES) {    // order: X1, X2, X3, Feature
        const body = { format: s.key, prompt: promptUsed };
        if (seed) body.reference_image_url = seed;
        setProgress(s.label);
        const { data } = await api.post("/admin/ad/generate", body);
        onResult(s.key, data.url);
        collected[s.key] = data.url;
        if (!refImg && !seed) seed = data.url;   // keep the other sizes visually consistent with the first
      }
      setResults(collected);
      setGenMeta({ prompt: promptUsed, seed: refImg || seed });   // reuse for single-size regen
      toast.success("Generated X1, X2, X3 + Feature ad");
    } catch { toast.error("Generation failed. Try again."); }
    finally { setBusy(false); setProgress(""); }
  };

  const regen = async (s) => {
    if (!genMeta || busy || regenKey) return;
    setRegenKey(s.key);
    try {
      const body = { format: s.key, prompt: genMeta.prompt };
      if (genMeta.seed) body.reference_image_url = genMeta.seed;
      const { data } = await api.post("/admin/ad/generate", body);
      onResult(s.key, data.url);
      setResults((r) => ({ ...r, [s.key]: data.url }));
      toast.success(`Regenerated ${s.label}`);
    } catch { toast.error("Regeneration failed. Try again."); }
    finally { setRegenKey(""); }
  };

  const del = (s) => {
    onResult(s.key, "");
    setResults((r) => { const n = { ...r }; delete n[s.key]; return n; });
  };

  return (
    <div className="border border-race-volt/40 bg-race-volt/5 rounded-md p-3 space-y-2" data-testid="cc-genall">
      <div className="flex items-center gap-2"><Sparkles size={14} className="text-race-volt" /><span className="text-sm font-semibold text-race-volt">Generate all sizes with AI</span></div>
      <p className="text-[11px] text-race-sub -mt-1">One prompt fills X1, X2, X3 and the Countdown Feature ad with a consistent look. Add a reference (e.g. a logo) for on-brand results.</p>
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
        placeholder={`Describe the campaign (e.g. "energy drink can bursting with lightning, dark carbon bg, logo space on the right")`}
        data-testid="cc-genall-prompt"
        className="w-full bg-race-bg border border-race-line rounded-sm px-2 py-1.5 text-xs text-race-txt outline-none focus:border-race-volt resize-none" />
      <input ref={refInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="cc-genall-reffile" onChange={(e) => doRefUpload(e.target.files?.[0])} />
      {refImg ? (
        <div className="flex items-center gap-2 text-[11px]" data-testid="cc-genall-ref">
          <div className="w-10 h-10 rounded-sm overflow-hidden border border-race-line shrink-0"><img src={srcFor(refImg)} alt="reference" className="w-full h-full object-cover" /></div>
          <span className="text-race-sub">Reference attached</span>
          <button type="button" onClick={() => setRefImg("")} data-testid="cc-genall-ref-remove"
            className="inline-flex items-center gap-1 text-race-muted hover:text-race-red"><Trash2 size={12} /> Remove</button>
        </div>
      ) : (
        <div role="button" tabIndex={0} onClick={() => refInput.current?.click()} data-testid="cc-genall-ref-btn"
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-race-volt", "text-race-volt"); }}
          onDragLeave={(e) => { e.currentTarget.classList.remove("border-race-volt", "text-race-volt"); }}
          onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-race-volt", "text-race-volt"); doRefUpload(e.dataTransfer.files?.[0]); }}
          className="w-full flex items-center justify-center gap-1.5 border border-dashed border-race-line rounded-sm py-3 text-[11px] text-race-sub hover:border-race-volt hover:text-race-volt transition-colors cursor-pointer disabled:opacity-50">
          {refBusy ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />} {refBusy ? "Uploading…" : "Reference image (optional) — drag & drop or click"}
        </div>
      )}
      <button type="button" onClick={run} disabled={busy} data-testid="cc-genall-go"
        className="w-full inline-flex items-center justify-center gap-2 bg-race-volt/90 hover:bg-race-volt text-race-bg font-semibold rounded-sm py-2 text-xs disabled:opacity-50 transition-colors">
        {busy ? <RefreshCw size={13} className="animate-spin" /> : <Sparkles size={13} />}{busy ? `Generating ${progress}…` : "Generate all sizes + Feature"}
      </button>
      {Object.keys(results).length > 0 && (
        <div className="space-y-3 pt-1" data-testid="cc-genall-results">
          {GEN_SIZES.map((s) => results[s.key] ? (
            <div key={s.key} className="space-y-1">
              <div className="text-[11px] text-race-muted text-center">{s.label}</div>
              <div className={`relative bg-race-bg border border-race-line rounded-sm overflow-hidden w-full ${s.key === "feature" ? "max-w-[140px] mx-auto" : ""}`} style={{ aspectRatio: `${s.w} / ${s.h}` }}>
                <img src={srcFor(results[s.key])} alt={s.label} onClick={() => setLightbox(srcFor(results[s.key]))}
                  className="w-full h-full object-cover cursor-zoom-in" data-testid={`cc-genall-preview-${s.key}`} />
                <button type="button" onClick={() => del(s)} disabled={busy || !!regenKey} data-testid={`cc-genall-del-${s.key}`}
                  title="Delete this banner"
                  className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center rounded-full bg-black/60 text-white hover:bg-race-red transition-colors disabled:opacity-40">
                  <Trash2 size={14} />
                </button>
              </div>
              <button type="button" onClick={() => regen(s)} disabled={busy || !!regenKey} data-testid={`cc-genall-regen-${s.key}`}
                className="w-full inline-flex items-center justify-center gap-1.5 text-xs text-race-volt hover:underline disabled:opacity-50 disabled:no-underline">
                <RefreshCw size={13} className={regenKey === s.key ? "animate-spin" : ""} />{regenKey === s.key ? "Redoing…" : "Regenerate"}
              </button>
            </div>
          ) : null)}
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightbox("")} data-testid="cc-genall-lightbox">
          <img src={lightbox} alt="Enlarged banner" className="max-w-full max-h-full object-contain rounded-md shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <button type="button" onClick={() => setLightbox("")} data-testid="cc-genall-lightbox-close"
            className="absolute top-4 right-4 w-10 h-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white">
            <X size={20} />
          </button>
        </div>
      )}
    </div>
  );
}

function CreativeSlot({ size, value, onChange, defaultPrompt = "" }) {
  const ref = useRef(null);
  const refInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genPrompt, setGenPrompt] = useState(defaultPrompt);
  const [genBusy, setGenBusy] = useState(false);
  const [refImg, setRefImg] = useState("");
  const [refBusy, setRefBusy] = useState(false);
  const ratio = size.w / size.h;

  const doRefUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image."); return; }
    setRefBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRefImg(data.url);
      toast.success("Reference image added");
    } catch { toast.error("Reference upload failed."); }
    finally { setRefBusy(false); if (refInput.current) refInput.current.value = ""; }
  };

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image (PNG, JPG, or WEBP)."); return; }
    const dims = await new Promise((res) => {
      const img = new Image();
      img.onload = () => res({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => res(null);
      img.src = URL.createObjectURL(file);
    });
    if (dims && Math.abs(dims.w / dims.h - ratio) >= 0.15) {
      toast.error(`${size.label} must be ${size.w}×${size.h} (that ratio). Yours is ${dims.w}×${dims.h}.`);
      if (ref.current) ref.current.value = "";
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success(`${size.label} uploaded`);
    } catch { toast.error("Upload failed. Try a smaller image."); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  const doGenerate = async () => {
    if (!genPrompt.trim()) { toast.error("Describe the ad you want to generate"); return; }
    setGenBusy(true);
    try {
      const body = { format: size.key, prompt: genPrompt.trim() };
      const reference = refImg || value;   // uploaded reference wins, else the current image
      if (reference) body.reference_image_url = reference;
      const { data } = await api.post("/admin/ad/generate", body);
      onChange(data.url);
      setGenOpen(false);
      toast.success(`${size.label} generated`);
    } catch { toast.error("AI generation failed. Try a different description."); }
    finally { setGenBusy(false); }
  };

  return (
    <div className="border border-race-line rounded-sm p-3" data-testid={`cc-creative-${size.key}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-race-txt">{size.label} <span className="text-race-sub font-normal">· {size.w}×{size.h}</span></span>
        <span className="text-[10px] uppercase tracking-wide text-race-muted">{size.note}</span>
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
        data-testid={`cc-creative-file-${size.key}`} onChange={(e) => doUpload(e.target.files?.[0])} />
      {value ? (
        <div className="flex items-center gap-3">
          <div className="bg-race-bg border border-race-line rounded-sm overflow-hidden shrink-0"
               style={{ width: 140, aspectRatio: `${size.w} / ${size.h}` }}>
            <img src={srcFor(value)} alt={size.label} className="w-full h-full object-cover" data-testid={`cc-creative-preview-${size.key}`} />
          </div>
          <div className="flex flex-col gap-1 text-[11px]">
            <button type="button" onClick={() => ref.current?.click()} className="text-race-volt hover:underline text-left" data-testid={`cc-creative-replace-${size.key}`}>Replace</button>
            <button type="button" onClick={() => { setGenPrompt(genPrompt || defaultPrompt); setGenOpen((o) => !o); }} className="text-race-volt hover:underline text-left inline-flex items-center gap-1" data-testid={`cc-creative-ai-${size.key}`}><Sparkles size={11} /> Generate with AI</button>
            <button type="button" onClick={() => onChange("")} className="text-race-sub hover:text-race-red text-left" data-testid={`cc-creative-remove-${size.key}`}>Remove</button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <button type="button" onClick={() => ref.current?.click()} disabled={busy}
            data-testid={`cc-creative-upload-${size.key}`}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-race-volt", "text-race-volt"); }}
            onDragLeave={(e) => { e.currentTarget.classList.remove("border-race-volt", "text-race-volt"); }}
            onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-race-volt", "text-race-volt"); doUpload(e.dataTransfer.files?.[0]); }}
            className="w-full flex items-center justify-center gap-2 border border-dashed border-race-line rounded-sm py-5 text-race-sub hover:border-race-volt hover:text-race-volt transition-colors disabled:opacity-50">
            {busy ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
            <span className="text-xs">{busy ? "Uploading…" : `Upload ${size.w}×${size.h}`}</span>
          </button>
          <button type="button" onClick={() => { setGenPrompt(genPrompt || defaultPrompt); setGenOpen((o) => !o); }} data-testid={`cc-creative-ai-${size.key}`}
            className="w-full flex items-center justify-center gap-2 border border-race-volt/50 text-race-volt hover:bg-race-volt/10 rounded-sm py-2 text-xs font-semibold transition-colors">
            <Sparkles size={13} /> Generate with AI
          </button>
        </div>
      )}
      {genOpen && (
        <div className="mt-2 border-t border-race-line/60 pt-2 space-y-2" data-testid={`cc-creative-ai-panel-${size.key}`}>
          <textarea value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} rows={2}
            placeholder={`Describe the ${size.label} (e.g. "bold sale banner for a coffee brand, warm colors, 'Order Now' button")`}
            data-testid={`cc-creative-ai-prompt-${size.key}`}
            className="w-full bg-race-bg border border-race-line rounded-sm px-2 py-1.5 text-xs text-race-txt outline-none focus:border-race-volt resize-none" />
          <input ref={refInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            data-testid={`cc-creative-ai-reffile-${size.key}`} onChange={(e) => doRefUpload(e.target.files?.[0])} />
          {refImg ? (
            <div className="flex items-center gap-2 text-[11px]" data-testid={`cc-creative-ai-ref-${size.key}`}>
              <div className="w-10 h-10 rounded-sm overflow-hidden border border-race-line shrink-0"><img src={srcFor(refImg)} alt="reference" className="w-full h-full object-cover" /></div>
              <span className="text-race-sub">Reference image attached</span>
              <button type="button" onClick={() => setRefImg("")} className="text-race-muted hover:text-race-red">Remove</button>
            </div>
          ) : (
            <button type="button" onClick={() => refInput.current?.click()} disabled={refBusy} data-testid={`cc-creative-ai-ref-btn-${size.key}`}
              className="inline-flex items-center gap-1.5 border border-race-line hover:border-race-volt text-race-sub hover:text-race-volt rounded-sm px-3 py-1.5 text-[11px] transition-colors disabled:opacity-50">
              {refBusy ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />} Reference image (optional)
            </button>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={doGenerate} disabled={genBusy} data-testid={`cc-creative-ai-go-${size.key}`}
              className="inline-flex items-center gap-1.5 bg-race-volt/90 hover:bg-race-volt text-race-bg font-semibold rounded-sm px-4 py-1.5 text-xs disabled:opacity-50">
              {genBusy ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}{genBusy ? "Generating…" : `Generate ${size.w}×${size.h}`}
            </button>
            <button type="button" onClick={() => setGenOpen(false)} className="text-race-sub hover:text-race-txt text-xs px-2">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function AdNetworkAdmin() {
  const [metrics, setMetrics] = useState(null);
  const [taxonomy, setTaxonomy] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const takeover = useTakeoverStatus(campaigns, "campaign_id");
  const [form, setForm] = useState({ name: "", sponsor: "", image_url: "", creatives: { single: "", double: "", triple: "" }, variant_b: { single: "", double: "", triple: "" }, ab_test: false, click_url: "", priority: 1, target_tags: [], placements: [...PLACEMENTS], cpm_usd: "", budget_usd: "", impression_cap: "", is_house: false });
  const [newTag, setNewTag] = useState({ tag: "", category: "" });
  const [segments, setSegments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [autoGenBusy, setAutoGenBusy] = useState(false);
  const [interestSearch, setInterestSearch] = useState("");
  const [showAllInterests, setShowAllInterests] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ cpm_usd: "", budget_usd: "", impression_cap: "", priority: 1, click_url: "", creatives: { single: "", double: "", triple: "" }, variants: [] });
  const [subTab, setSubTab] = useState("overview");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const savedPrefs = (() => { try { return JSON.parse(localStorage.getItem("adCampPrefs") || "{}"); } catch { return {}; } })();
  const [search, setSearch] = useState(savedPrefs.search || "");
  const [statusFilter, setStatusFilter] = useState(savedPrefs.statusFilter || "all");
  const [targetFilter, setTargetFilter] = useState(savedPrefs.targetFilter || "all");
  const [sortBy, setSortBy] = useState(savedPrefs.sortBy || "recent");
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    localStorage.setItem("adCampPrefs", JSON.stringify({ search, statusFilter, targetFilter, sortBy }));
  }, [search, statusFilter, targetFilter, sortBy]);

  const load = useCallback(() => {
    api.get("/ads/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    api.get("/ads/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
    api.get("/ads/admin/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
    api.get("/ads/admin/segments").then((r) => setSegments(r.data.segments || [])).catch(() => {});
  }, []);
  const tj = useTakeoverJob(() => load());
  useEffect(() => { load(); }, [load]);

  const createCampaign = () => {
    if (!canCreate) return;
    setSubmitting(true);
    const bVals = Object.values(form.variant_b || {}).filter(Boolean);
    const variants = form.ab_test && bVals.length > 0
      ? [{ id: "A", label: "Variant A", creatives: form.creatives },
         { id: "B", label: "Variant B", creatives: form.variant_b }]
      : null;
    const { variant_b, ab_test, ...rest } = form;
    const payload = {
      ...rest,
      variants,
      cpm_usd: form.cpm_usd === "" ? null : Number(form.cpm_usd),
      budget_usd: form.budget_usd === "" ? null : Number(form.budget_usd),
      impression_cap: form.impression_cap === "" ? null : Number(form.impression_cap),
    };
    api.post("/ads/admin/campaigns", payload)
      .then(() => {
        // Reset only after a confirmed success so nothing is lost on error.
        setForm({ name: "", sponsor: "", image_url: "", creatives: { single: "", double: "", triple: "" }, variant_b: { single: "", double: "", triple: "" }, ab_test: false, click_url: "", priority: 1, target_tags: [], placements: [...PLACEMENTS], cpm_usd: "", budget_usd: "", impression_cap: "", is_house: false });
        setInterestSearch(""); setShowAllInterests(false);
        setDrawerOpen(false);
        toast.success("Campaign created successfully.");
        load();
      })
      .catch((e) => { toast.error(apiError(e?.response?.data?.detail) || "Could not create campaign. Please check the fields and try again."); })
      .finally(() => setSubmitting(false));
  };

  const taxOp = (op, tag, extra = {}) => api.post("/ads/admin/taxonomy", { op, tag, ...extra }).then(() => load()).catch(() => {});

  const hasAnyCreative = BANNER_SIZES.some((s) => form.creatives[s.key]);
  const missingSizes = BANNER_SIZES.filter((s) => !form.creatives[s.key]);
  const autoGenFor = async (key) => {
    const set = form[key];
    const ref = set.double || set.single || set.triple;
    if (!ref) { toast.error("Upload at least one banner first to use as the reference"); return; }
    const missing = BANNER_SIZES.filter((s) => !set[s.key]);
    if (!missing.length) { toast.info("All three sizes are already set"); return; }
    setAutoGenBusy(true);
    try {
      for (const s of missing) {
        const { data } = await api.post("/admin/ad/generate", { format: s.key, reference_image_url: ref, prompt: form.sponsor || form.name || "" });
        setForm((f) => ({ ...f, [key]: { ...f[key], [s.key]: data.url } }));
      }
      toast.success("Generated the missing banner sizes");
    } catch { toast.error("Auto-generate failed. Try again or upload manually."); }
    finally { setAutoGenBusy(false); }
  };
  const autoGenSizes = () => autoGenFor("creatives");

  const replaceTakeover = (c) => {
    startEdit(c);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="ad-camp-edit-creatives-${c.campaign_id}"] [data-testid="cc-creative-feature"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-amber-500"); setTimeout(() => el.classList.remove("ring-2", "ring-amber-500"), 2500); }
    }, 150);
  };
  const startEdit = (c) => {
    setEditing(c.campaign_id);
    const cr = c.creatives || {};
    setEditForm({
      cpm_usd: c.cpm_usd ?? "", budget_usd: c.budget_usd ?? "", impression_cap: c.impression_cap ?? "", priority: c.priority ?? 1,
      click_url: c.click_url || "",
      creatives: { single: cr.single || "", double: cr.double || "", triple: cr.triple || "", feature: cr.feature || "" },
      variants: (c.variants || []).map((v) => ({
        id: v.id, label: v.label,
        creatives: { single: v.creatives?.single || "", double: v.creatives?.double || "", triple: v.creatives?.triple || "" },
      })),
    });
  };
  const saveEdit = (cid) => {
    const payload = { priority: Number(editForm.priority) || 1 };
    if (editForm.cpm_usd !== "") payload.cpm_usd = Number(editForm.cpm_usd);
    payload.budget_usd = editForm.budget_usd === "" ? 0 : Number(editForm.budget_usd);
    payload.impression_cap = editForm.impression_cap === "" ? 0 : Number(editForm.impression_cap);
    payload.click_url = editForm.click_url || "";
    const cr = editForm.creatives || {};
    if (Object.values(cr).some(Boolean)) {
      payload.creatives = cr;
      payload.image_url = cr.double || cr.single || cr.triple;   // refresh list thumbnail too
    }
    if ((editForm.variants || []).length >= 2) payload.variants = editForm.variants;
    api.patch(`/ads/admin/campaigns/${cid}`, payload).then(() => { setEditing(null); load(); toast.success("Campaign updated"); }).catch(() => toast.error("Update failed"));
  };
  const toggleStatus = (c) => {
    const status = c.status === "paused" ? "active" : "paused";
    api.post(`/ads/admin/campaigns/${c.campaign_id}/status`, { status }).then(load).catch(() => toast.error("Failed"));
  };
  const del = (cid) => {
    if (!window.confirm("Delete this campaign? This cannot be undone.")) return;
    api.delete(`/ads/admin/campaigns/${cid}`).then(load).catch(() => toast.error("Delete failed"));
  };

  const toggleSelect = (cid) => setSelected((s) => (s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]));
  const bulkStatus = (status) => {
    if (!selected.length) return;
    api.post("/ads/admin/campaigns/bulk-status", { campaign_ids: selected, status })
      .then((r) => { toast.success(`${r.data.updated} campaign(s) ${status === "paused" ? "paused" : "resumed"}`); setSelected([]); load(); })
      .catch(() => toast.error("Bulk action failed"));
  };
  const topUp = (c) => {
    const input = window.prompt(`Top up budget for "${c.name}" (USD to add). The campaign will resume serving immediately.`, "50");
    if (input == null) return;
    const amt = Number(input);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    api.post(`/ads/admin/campaigns/${c.campaign_id}/topup`, { amount_usd: amt })
      .then(() => { toast.success(`Added $${amt} to "${c.name}" - back on air`); load(); })
      .catch(() => toast.error("Top-up failed"));
  };

  const imgSrc = (u) => (u ? (u.startsWith("http") ? u : `${BE}${u}`) : null);
  const budgetStatus = (c) => {
    if (c.budget_usd == null || c.budget_usd <= 0) return { border: "border-race-line", badge: null };
    const pct = (c.spent_usd || 0) / c.budget_usd;
    if (pct >= 1) return { border: "border-race-red", badge: "budget spent", badgeClass: "border border-race-red text-race-red" };
    if (pct >= 0.8) return { border: "border-amber-400/60", badge: `${Math.round(pct * 100)}% spent`, badgeClass: "border border-amber-400/60 text-amber-400" };
    return { border: "border-race-line", badge: null };
  };
  const filteredCampaigns = campaigns
    .filter((c) => { const q = search.trim().toLowerCase(); return !q || c.name.toLowerCase().includes(q) || (c.sponsor || "").toLowerCase().includes(q); })
    .filter((c) => statusFilter === "all" || (statusFilter === "paused" ? c.status === "paused" : c.status !== "paused"))
    .filter((c) => targetFilter === "all" || (targetFilter === "targeted" ? (c.target_tags || []).length > 0 : (c.target_tags || []).length === 0))
    .slice().sort((a, b) => {
      if (sortBy === "spend") return (b.spent_usd || 0) - (a.spent_usd || 0);
      if (sortBy === "impressions") return (b.impressions || 0) - (a.impressions || 0);
      if (sortBy === "name") return a.name.localeCompare(b.name);
      return 0;
    });

  const allTags = taxonomy.flatMap((c) => c.tags);
  const toggleTag = (tag) => setForm((f) => ({ ...f, target_tags: f.target_tags.includes(tag) ? f.target_tags.filter((t) => t !== tag) : [...f.target_tags, tag] }));

  // Inline validation (mirrors backend rules: only `name` is required).
  const isValidUrl = (u) => { try { const x = new URL(u); return x.protocol === "http:" || x.protocol === "https:"; } catch { return false; } };
  const urlError = form.click_url.trim() !== "" && !isValidUrl(form.click_url.trim());
  const cpmError = form.cpm_usd !== "" && (isNaN(Number(form.cpm_usd)) || Number(form.cpm_usd) < 0);
  const budgetError = form.budget_usd !== "" && (isNaN(Number(form.budget_usd)) || Number(form.budget_usd) < 0);
  const capError = form.impression_cap !== "" && (isNaN(Number(form.impression_cap)) || Number(form.impression_cap) < 0);
  const canCreate = form.name.trim() !== "" && !urlError && !cpmError && !budgetError && !capError && !submitting;

  const inpCls = "w-full bg-race-bg border border-race-line rounded-sm px-3 py-2 text-sm focus:border-race-red outline-none transition-colors";
  const errCls = "w-full bg-race-bg border border-race-red rounded-sm px-3 py-2 text-sm outline-none";
  const popularTags = allTags.filter((t) => !form.target_tags.includes(t)).slice(0, 8);
  const searchedTags = interestSearch.trim() ? allTags.filter((t) => t.toLowerCase().includes(interestSearch.trim().toLowerCase())) : [];
  const tagBtn = (t) => (
    <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`ad-camp-tag-${t}`}
      className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${form.target_tags.includes(t) ? "bg-race-red text-race-bg border-race-red" : "border-race-line text-race-sub hover:border-race-txt hover:text-race-txt"}`}>{t}</button>
  );

  return (
    <AdminShell>
    <div className={`${subTab === "taxonomy" ? "max-w-[1200px]" : "max-w-5xl"}`} data-testid="ad-network-admin">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2"><Megaphone className="text-race-red" /><h1 className="text-2xl font-heading">Ad Network</h1></div>
        <button onClick={load} data-testid="ad-admin-refresh" className="flex items-center gap-2 text-sm text-race-sub hover:text-race-txt"><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="flex gap-1 mb-6 border-b border-race-line" data-testid="ad-subtabs">
        {[["overview", "Overview"], ["campaigns", `Campaigns (${campaigns.length})`], ["taxonomy", "Taxonomy"]].map(([v, l]) => (
          <button key={v} onClick={() => setSubTab(v)} data-testid={`ad-subtab-${v}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${subTab === v ? "border-race-txt text-race-txt" : "border-transparent text-race-sub hover:text-race-txt"}`}>{l}</button>
        ))}
      </div>

      {subTab === "overview" && (<>
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8" data-testid="ad-metrics">
          <Stat label="Members w/ interests" value={`${metrics.pct_with_explicit_interest}%`} />
          <Stat label="Impressions" value={metrics.impressions_total} />
          <Stat label="Clicks / CTR" value={`${metrics.clicks_total ?? 0} · ${metrics.ctr_pct ?? 0}%`} />
          <Stat label="Personalized match rate" value={`${metrics.match_rate_targeted_pct}%`} />
          <Stat label="Feedback actions" value={metrics.feedback_actions} />
          <Stat label="Opt-outs" value={metrics.opt_out_count} />
        </div>
      )}

      {/* Segment revenue view - sponsor performance by interest segment (pricing) */}
      <div className="bg-race-surface border border-race-line rounded-lg p-5 mb-8" data-testid="ad-segments">
        <h2 className="text-base font-semibold mb-1">Segment performance</h2>
        <p className="text-xs text-race-sub mb-3">Audience size vs. advertiser demand per interest - use the demand index to price inventory (higher = scarce, in-demand → charge more). <a href="/rate-card" data-testid="rate-card-link" className="text-race-volt underline">View sponsor rate card →</a></p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-race-sub border-b border-race-line">
                <th className="py-2 pr-3">Segment</th>
                <th className="py-2 px-3 text-right">Audience</th>
                <th className="py-2 px-3 text-right">Campaigns</th>
                <th className="py-2 px-3 text-right">Impressions</th>
                <th className="py-2 px-3 text-right">👍 / 👎</th>
                <th className="py-2 px-3 text-right">Demand index</th>
                <th className="py-2 pl-3 text-right">Suggested CPM</th>
              </tr>
            </thead>
            <tbody>
              {segments.length === 0 ? (
                <tr><td colSpan={7} className="py-4 text-center text-race-sub">No segment activity yet - data appears as members pick interests and ads serve.</td></tr>
              ) : segments.map((s) => (
                <tr key={s.tag} className="border-b border-race-line/50" data-testid={`ad-segment-${s.tag}`}>
                  <td className="py-2 pr-3 font-medium text-race-txt">{s.tag}</td>
                  <td className="py-2 px-3 text-right">{s.audience}</td>
                  <td className="py-2 px-3 text-right">{s.campaigns}</td>
                  <td className="py-2 px-3 text-right">{s.impressions}</td>
                  <td className="py-2 px-3 text-right text-race-sub">{s.positive} / {s.negative}</td>
                  <td className={`py-2 px-3 text-right font-mono ${s.demand_index >= 1 ? "text-race-volt" : "text-race-sub"}`}>{s.demand_index}</td>
                  <td className="py-2 pl-3 text-right font-mono text-race-txt" data-testid={`ad-cpm-${s.tag}`}>${s.suggested_cpm_usd?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>)}

      {subTab === "campaigns" && (
      <div data-testid="ad-campaigns-tab">
        {/* Toolbar: search, filters, sort, + New Campaign drawer */}
        <div className="flex flex-wrap gap-2 items-center justify-between mb-4">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-race-sub" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search campaigns…" data-testid="ad-camp-search"
                className="bg-race-bg border border-race-line rounded-sm pl-8 pr-3 py-1.5 text-sm w-56" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="ad-camp-filter-status" className="bg-race-bg border border-race-line rounded-sm px-2 py-1.5 text-sm">
              <option value="all">All status</option><option value="active">Active</option><option value="paused">Paused</option>
            </select>
            <select value={targetFilter} onChange={(e) => setTargetFilter(e.target.value)} data-testid="ad-camp-filter-target" className="bg-race-bg border border-race-line rounded-sm px-2 py-1.5 text-sm">
              <option value="all">All types</option><option value="targeted">Personalized</option><option value="untargeted">General</option>
            </select>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="ad-camp-sort" className="bg-race-bg border border-race-line rounded-sm px-2 py-1.5 text-sm">
              <option value="recent">Newest</option><option value="spend">Top spend</option><option value="impressions">Most impressions</option><option value="name">Name A–Z</option>
            </select>
          </div>
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button data-testid="ad-new-campaign-btn" className="flex items-center gap-2 bg-race-red text-race-bg font-semibold rounded-sm px-4 py-2 text-sm"><Plus size={15} /> New Campaign</button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col bg-race-surface border-race-line text-race-txt">
              <SheetHeader className="px-5 pt-5 pb-3 border-b border-race-line shrink-0">
                <SheetTitle className="text-race-txt font-heading text-2xl tracking-tight">NEW CAMPAIGN</SheetTitle>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
                {/* 1. CAMPAIGN DETAILS */}
                <section className="space-y-3" data-testid="cc-section-details">
                  <div className="overline text-race-txt">Campaign Details</div>
                  <div>
                    <label className="overline text-race-sub block mb-1">Campaign Name <span className="text-race-red">*</span></label>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Enter campaign name" data-testid="ad-camp-name" className={inpCls} />
                  </div>
                  <div>
                    <label className="overline text-race-sub block mb-1">Sponsor</label>
                    <input value={form.sponsor} onChange={(e) => setForm({ ...form, sponsor: e.target.value })}
                      placeholder="Enter sponsor name" data-testid="ad-camp-sponsor" className={inpCls} />
                  </div>
                  <div>
                    <label className="overline text-race-sub block mb-1">Click-Through URL</label>
                    <input value={form.click_url} onChange={(e) => setForm({ ...form, click_url: e.target.value })}
                      placeholder="https://" data-testid="ad-camp-click_url" className={urlError ? errCls : inpCls} />
                    {urlError && <p className="text-race-red text-[11px] mt-1" data-testid="ad-camp-url-error">Enter a valid URL (including https://).</p>}
                  </div>
                </section>

                {/* 2. CREATIVE */}
                <section className="space-y-3" data-testid="cc-section-creative">
                  <div className="overline text-race-txt">Creative</div>
                  <p className="text-[11px] text-race-sub -mt-1">Upload a banner for each size. The best-fitting one is served automatically per placement.</p>
                  <GenAllSizes defaultPrompt={form.sponsor || form.name}
                    onResult={(key, url) => setForm((f) => ({ ...f, creatives: { ...f.creatives, [key]: url } }))} />
                  {BANNER_SIZES.map((s) => (
                    <CreativeSlot key={s.key} size={s} value={form.creatives[s.key]} defaultPrompt={form.sponsor || form.name}
                      onChange={(url) => setForm((f) => ({ ...f, creatives: { ...f.creatives, [s.key]: url } }))} />
                  ))}
                  <CreativeSlot size={FEATURE_SIZE} value={form.creatives.feature} defaultPrompt={form.sponsor || form.name}
                    onChange={(url) => setForm((f) => ({ ...f, creatives: { ...f.creatives, feature: url } }))} />
                  <FeatureAdPreview src={srcFor(form.creatives.feature)} className="py-2" />
                  {hasAnyCreative && missingSizes.length > 0 && (
                    <button type="button" onClick={autoGenSizes} disabled={autoGenBusy} data-testid="cc-autogen-sizes"
                      className="w-full flex items-center justify-center gap-2 border border-race-volt/60 text-race-volt hover:bg-race-volt/10 rounded-sm py-2 text-xs font-semibold disabled:opacity-50 transition-colors">
                      {autoGenBusy ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      {autoGenBusy ? "Generating…" : `Auto-generate ${missingSizes.map((s) => s.label).join(" & ")} from my banner`}
                    </button>
                  )}
                  {hasAnyCreative && (
                    <div className="mt-2 border-t border-race-line pt-3" data-testid="cc-placement-preview">
                      <div className="overline text-race-sub mb-2">Placement preview</div>
                      <div className="space-y-3">
                        {BANNER_SIZES.filter((s) => form.creatives[s.key]).map((s) => (
                          <div key={s.key} data-testid={`cc-preview-${s.key}`}>
                            <div className="text-[10px] uppercase tracking-wide text-race-muted mb-1">{s.label} → {SIZE_PLACEMENTS[s.key].join(" · ")}</div>
                            <div className="relative bg-race-surface border border-race-line rounded-lg overflow-hidden" style={{ maxWidth: s.w }}>
                              <img src={srcFor(form.creatives[s.key])} alt={s.label} className="w-full object-cover" style={{ aspectRatio: `${s.w} / ${s.h}` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {!form.is_house && hasAnyCreative && (
                    <div className="mt-2 border-t border-race-line pt-3" data-testid="cc-ab-block">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox" checked={form.ab_test} onChange={(e) => setForm((f) => ({ ...f, ab_test: e.target.checked }))} data-testid="cc-ab-toggle" className="accent-race-volt" />
                        <span className="text-sm font-semibold text-race-txt">A/B test a second creative</span>
                      </label>
                      <p className="text-[11px] text-race-sub mt-1">Upload a Variant B. Both compete on live traffic and the higher click-through rate is automatically served more.</p>
                      {form.ab_test && (
                        <div className="mt-3 space-y-3" data-testid="cc-variant-b">
                          <div className="text-[10px] uppercase tracking-wide text-race-volt font-semibold">Variant B</div>
                          {BANNER_SIZES.map((s) => (
                            <CreativeSlot key={s.key} size={s} value={form.variant_b[s.key]} defaultPrompt={form.sponsor || form.name}
                              onChange={(url) => setForm((f) => ({ ...f, variant_b: { ...f.variant_b, [s.key]: url } }))} />
                          ))}
                          {BANNER_SIZES.some((s) => form.variant_b[s.key]) && BANNER_SIZES.some((s) => !form.variant_b[s.key]) && (
                            <button type="button" onClick={() => autoGenFor("variant_b")} disabled={autoGenBusy} data-testid="cc-autogen-sizes-b"
                              className="w-full flex items-center justify-center gap-2 border border-race-volt/60 text-race-volt hover:bg-race-volt/10 rounded-sm py-2 text-xs font-semibold disabled:opacity-50 transition-colors">
                              {autoGenBusy ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
                              {autoGenBusy ? "Generating…" : "Auto-generate missing sizes for Variant B"}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </section>
                {!form.is_house && (
                  <section className="space-y-2" data-testid="cc-section-targeting">
                    <div className="overline text-race-txt">Ad Personalization</div>
                    <div>
                      <label className="overline text-race-sub block">Personalization Interests</label>
                      <p className="text-[11px] text-race-sub mt-0.5">Optional. Leave empty to reach all eligible users.</p>
                    </div>
                    {form.target_tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5" data-testid="ad-camp-selected">
                        <span className="text-[10px] uppercase tracking-wider text-race-sub w-full">Selected</span>
                        {form.target_tags.map((t) => (
                          <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`ad-camp-selected-${t}`}
                            className="text-[11px] rounded-full pl-2.5 pr-1.5 py-1 bg-race-red text-race-bg border border-race-red flex items-center gap-1">{t}<X size={11} /></button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-race-sub" data-testid="ad-camp-general-note">General audience - no interests selected. Campaign will reach all eligible users.</p>
                    )}
                    <div className="relative">
                      <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-race-sub pointer-events-none" />
                      <input value={interestSearch} onChange={(e) => setInterestSearch(e.target.value)} placeholder="Search interests…"
                        data-testid="ad-camp-interest-search" className="w-full bg-race-bg border border-race-line rounded-sm pl-8 pr-3 py-2 text-sm focus:border-race-red outline-none" />
                    </div>
                    {interestSearch.trim() ? (
                      <div className="flex flex-wrap gap-1.5" data-testid="ad-camp-search-results">
                        {searchedTags.length ? searchedTags.map((t) => tagBtn(t)) : <span className="text-[11px] text-race-sub">No matching interests.</span>}
                      </div>
                    ) : (
                      <>
                        <div className="text-[10px] uppercase tracking-wider text-race-sub">Popular interests</div>
                        <div className="flex flex-wrap gap-1.5">{popularTags.map((t) => tagBtn(t))}</div>
                        <button type="button" onClick={() => setShowAllInterests((v) => !v)} data-testid="ad-camp-viewall"
                          className="text-[11px] text-race-volt hover:underline">{showAllInterests ? "Hide all interests ↑" : "View all interests ↓"}</button>
                        {showAllInterests && (
                          <div className="border border-race-line rounded-sm p-2 max-h-40 overflow-y-auto flex flex-wrap gap-1.5" data-testid="ad-camp-all-interests">
                            {allTags.map((t) => tagBtn(t))}
                          </div>
                        )}
                      </>
                    )}
                    {form.target_tags.length > 0 && (() => {
                      const priced = form.target_tags.map((t) => (segments.find((s) => s.tag === t)?.suggested_cpm_usd) || 5).filter(Boolean);
                      const cpm = priced.length ? Math.max(...priced) : 5;
                      return (
                        <div className="text-xs text-race-volt bg-race-volt/10 border border-race-volt/30 rounded-sm px-3 py-2" data-testid="ad-camp-price-hint">
                          Suggested rate for these segments: <b>${cpm.toFixed(2)} CPM</b> (per 1,000 impressions). Higher-demand segments cost more.
                        </div>
                      );
                    })()}
                  </section>
                )}

                {/* 4. DELIVERY & SPEND */}
                <section className="space-y-3" data-testid="cc-section-delivery">
                  <div className="overline text-race-txt">Delivery &amp; Spend</div>
                  {form.is_house ? (
                    <p className="text-[11px] text-race-sub" data-testid="ad-camp-house-spend-note">House ads are not charged - CPM, budget and impression caps don't apply.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="overline text-race-sub block mb-1">CPM Rate</label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-race-sub text-sm pointer-events-none">$</span>
                          <input type="number" min="0" step="0.5" value={form.cpm_usd} onChange={(e) => setForm({ ...form, cpm_usd: e.target.value })}
                            placeholder="5.00" data-testid="ad-camp-cpm" className={`w-full bg-race-bg border rounded-sm pl-6 pr-2 py-1.5 text-sm outline-none ${cpmError ? "border-race-red" : "border-race-line focus:border-race-red"}`} />
                        </div>
                        <div className="text-[10px] text-race-sub mt-0.5">{cpmError ? <span className="text-race-red">Enter a valid CPM</span> : "Cost per 1,000 impressions"}</div>
                      </div>
                      <div>
                        <label className="overline text-race-sub block mb-1">Budget</label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-race-sub text-sm pointer-events-none">$</span>
                          <input type="number" min="0" step="1" value={form.budget_usd} onChange={(e) => setForm({ ...form, budget_usd: e.target.value })}
                            placeholder="10,000" data-testid="ad-camp-budget" className={`w-full bg-race-bg border rounded-sm pl-6 pr-2 py-1.5 text-sm outline-none ${budgetError ? "border-race-red" : "border-race-line focus:border-race-red"}`} />
                        </div>
                        <div className="text-[10px] text-race-sub mt-0.5">{budgetError ? <span className="text-race-red">Must be 0 or more</span> : "Leave blank for unlimited"}</div>
                      </div>
                      <div>
                        <label className="overline text-race-sub block mb-1">Impr. Cap</label>
                        <input type="number" min="0" step="1" value={form.impression_cap} onChange={(e) => setForm({ ...form, impression_cap: e.target.value })}
                          placeholder="Unlimited" data-testid="ad-camp-impression-cap" className={`w-full bg-race-bg border rounded-sm px-2 py-1.5 text-sm outline-none ${capError ? "border-race-red" : "border-race-line focus:border-race-red"}`} />
                        <div className="text-[10px] text-race-sub mt-0.5">{capError ? <span className="text-race-red">Must be 0 or more</span> : "Max impressions"}</div>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="overline text-race-sub block mb-1">Priority</label>
                    <input type="number" min="1" value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                      data-testid="ad-camp-priority" className="w-24 bg-race-bg border border-race-line rounded-sm px-3 py-1.5 text-sm focus:border-race-red outline-none" />
                    <div className="text-[10px] text-race-sub mt-0.5">Higher numbers win when multiple campaigns are eligible.</div>
                  </div>
                </section>

                {/* 5. HOUSE AD / FALLBACK */}
                <section className="space-y-2" data-testid="cc-section-house">
                  <div className="overline text-race-txt">House Ad / Fallback</div>
                  <label className="flex items-start gap-2 p-3 border border-race-line rounded-sm cursor-pointer hover:border-race-volt transition-colors" data-testid="ad-camp-house-row">
                    <input type="checkbox" checked={!!form.is_house} data-testid="ad-camp-house"
                      onChange={(e) => setForm({ ...form, is_house: e.target.checked })} className="mt-0.5 accent-race-red" />
                    <span className="text-xs">
                      <span className="text-race-txt font-semibold">Use as fallback campaign</span>
                      <span className="block text-race-sub mt-0.5">Fills eligible inventory when no paid or personalized campaign wins. House ads are not charged.</span>
                    </span>
                  </label>
                </section>
              </div>

              {/* Sticky footer */}
              <div className="shrink-0 border-t border-race-line bg-race-surface px-5 py-4 flex items-center justify-between gap-3" data-testid="ad-camp-footer">
                <button type="button" onClick={() => setDrawerOpen(false)} data-testid="ad-camp-cancel"
                  className="text-race-sub hover:text-race-txt text-sm px-3 py-2 transition-colors">Cancel</button>
                <button onClick={createCampaign} disabled={!canCreate} data-testid="ad-camp-create"
                  className="bg-white hover:bg-race-txt disabled:opacity-40 disabled:cursor-not-allowed text-race-bg font-semibold rounded-sm px-5 py-2 text-sm flex items-center gap-2 transition-colors">
                  {submitting ? "CREATING…" : (<><Plus size={15} /> CREATE CAMPAIGN</>)}
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Full-width campaign list */}
        <div className="bg-race-surface border border-race-line rounded-lg p-5" data-testid="ad-campaigns-panel">
          <h2 className="font-semibold mb-3">Campaigns ({filteredCampaigns.length}{filteredCampaigns.length !== campaigns.length ? ` of ${campaigns.length}` : ""})</h2>
          {(() => {
            const allVisibleIds = filteredCampaigns.map((c) => c.campaign_id);
            const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selected.includes(id));
            const toggleAll = () => setSelected(allSelected ? selected.filter((id) => !allVisibleIds.includes(id)) : Array.from(new Set([...selected, ...allVisibleIds])));
            return (
              <div className="flex items-center gap-3 mb-3 text-xs flex-wrap">
                <label className="flex items-center gap-1.5 text-race-sub cursor-pointer">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="ad-camp-select-all" /> Select all
                </label>
                {selected.length > 0 && (
                  <div className="flex items-center gap-2" data-testid="ad-bulk-bar">
                    <span className="text-race-txt">{selected.length} selected</span>
                    <button onClick={() => bulkStatus("paused")} data-testid="ad-bulk-pause" className="flex items-center gap-1 border border-race-line rounded-sm px-2 py-1 hover:border-amber-400 hover:text-amber-400"><Pause size={12} /> Pause</button>
                    <button onClick={() => bulkStatus("active")} data-testid="ad-bulk-resume" className="flex items-center gap-1 border border-race-line rounded-sm px-2 py-1 hover:border-race-volt hover:text-race-volt"><Play size={12} /> Resume</button>
                    <button onClick={() => setSelected([])} data-testid="ad-bulk-clear" className="text-race-sub hover:text-race-txt">Clear</button>
                  </div>
                )}
              </div>
            );
          })()}
          <TakeoverRegenPanel tj={tj} />
          <TakeoverSummary count={takeover.badCount} />
          <div className="space-y-2">
            {filteredCampaigns.map((c, ci) => {
              const bs = budgetStatus(c);
              return (
              <div key={`${c.campaign_id}-${ci}`} data-testid={`ad-camp-${c.campaign_id}`} className={`text-sm border rounded-sm px-3 py-2 ${bs.border}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex gap-3 min-w-0">
                    <input type="checkbox" checked={selected.includes(c.campaign_id)} onChange={() => toggleSelect(c.campaign_id)} data-testid={`ad-camp-select-${c.campaign_id}`} className="mt-1 shrink-0" />
                    {c.image_url ? (
                      <img src={imgSrc(c.image_url)} alt="" data-testid={`ad-camp-thumb-${c.campaign_id}`} className="h-12 w-20 object-cover rounded-sm border border-race-line shrink-0" />
                    ) : (
                      <div className="h-12 w-20 rounded-sm border border-race-line bg-race-bg flex items-center justify-center shrink-0"><ImageIcon size={16} className="text-race-sub" /></div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{c.name}</span>
                        {c.is_house && <span className="text-[9px] uppercase font-mono border border-race-volt/60 text-race-volt rounded-full px-1.5 py-0.5" data-testid={`ad-camp-house-badge-${c.campaign_id}`}>House</span>}
                        {c.sponsor && <span className="text-[11px] text-race-sub">· {c.sponsor}</span>}
                        {c.status === "paused" && <span className="text-[9px] uppercase font-mono border border-amber-400/50 text-amber-400 rounded-full px-1.5 py-0.5" data-testid={`ad-camp-paused-${c.campaign_id}`}>paused</span>}
                        {bs.badge && <span className={`text-[9px] uppercase font-mono rounded-full px-1.5 py-0.5 ${bs.badgeClass}`} data-testid={`ad-camp-budget-alert-${c.campaign_id}`}>{bs.badge}</span>}
                        <TakeoverBadge st={takeover.get(c)} id={c.campaign_id} onReplace={() => replaceTakeover(c)} />
                        {c.creatives?.feature && <button onClick={() => tj.start([c.campaign_id])} disabled={tj.running} data-testid={`takeover-regen-one-${c.campaign_id}`}
                          className="text-[10px] font-mono text-race-volt hover:underline disabled:opacity-40">Regenerate takeover</button>}
                        {bs.badge && <button onClick={() => topUp(c)} data-testid={`ad-camp-topup-${c.campaign_id}`} className="text-[9px] uppercase font-mono rounded-full px-1.5 py-0.5 border border-race-volt text-race-volt hover:bg-race-volt hover:text-race-bg transition-colors">Top up</button>}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(c.target_tags || []).length ? c.target_tags.map((t) => (
                          <span key={t} className="text-[10px] rounded-full px-1.5 py-0.5 border border-race-line text-race-sub">{t}</span>
                        )) : <span className="text-[10px] text-race-sub">General (not personalized)</span>}
                      </div>
                      <div className="text-[11px] text-race-sub mt-1">
                        ${(c.cpm_usd ?? 5).toFixed(2)} CPM · prio {c.priority} · {c.budget_usd != null ? `$${(c.spent_usd || 0).toFixed(2)}/$${c.budget_usd.toFixed(2)}` : "no budget"}{c.impression_cap != null ? ` · cap ${c.charged_impressions || 0}/${c.impression_cap}` : ""}
                      </div>
                      {(c.variants || []).length >= 2 && (() => {
                        const totalImps = c.variants.reduce((s, v) => s + (v.impressions || 0), 0);
                        const best = Math.max(...c.variants.map((v) => v.perf_multiplier || 1));
                        const leaderCount = c.variants.filter((v) => (v.perf_multiplier || 1) === best).length;
                        const hasLeader = totalImps > 0 && leaderCount === 1;
                        return (
                          <div className="flex flex-wrap gap-2 mt-1.5" data-testid={`ad-camp-variants-${c.campaign_id}`}>
                            {c.variants.map((v) => {
                              const lead = hasLeader && (v.perf_multiplier || 1) === best;
                              return (
                                <span key={v.id} className={`text-[10px] font-mono rounded px-1.5 py-0.5 border ${lead ? "border-race-volt text-race-volt" : "border-race-line text-race-sub"}`}>
                                  A/B {v.id}: {v.impressions || 0}imp · {v.clicks || 0}clk · {v.ctr || 0}%{lead ? " · winning" : ""}
                                </span>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-xs text-race-sub mr-1" data-testid={`ad-camp-stats-${c.campaign_id}`}>
                      {c.impressions} imp · {c.clicks || 0} clk · {c.ctr || 0}%
                      {c.perf_multiplier && Math.abs(c.perf_multiplier - 1) >= 0.05 && (
                        <span title="CTR-aware serving: how often this ad is shown vs. an average ad, based on its click-through rate"
                          className={`ml-1.5 font-mono ${c.perf_multiplier > 1 ? "text-race-volt" : "text-amber-400"}`}>
                          {c.perf_multiplier > 1 ? "▲" : "▼"}{c.perf_multiplier}× served
                        </span>
                      )}
                    </span>
                    <button onClick={() => startEdit(c)} data-testid={`ad-camp-edit-${c.campaign_id}`} title="Edit" className="text-race-sub hover:text-race-volt p-1"><Pencil size={13} /></button>
                    <button onClick={() => toggleStatus(c)} data-testid={`ad-camp-toggle-${c.campaign_id}`} title={c.status === "paused" ? "Resume" : "Pause"} className="text-race-sub hover:text-race-txt p-1">{c.status === "paused" ? <Play size={13} /> : <Pause size={13} />}</button>
                    <button onClick={() => del(c.campaign_id)} data-testid={`ad-camp-delete-${c.campaign_id}`} title="Delete" className="text-race-sub hover:text-race-red p-1"><Trash2 size={13} /></button>
                  </div>
                </div>
                {editing === c.campaign_id && (
                  <div className="mt-2 pt-2 border-t border-race-line grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`ad-camp-edit-panel-${c.campaign_id}`}>
                    <label className="text-[10px] text-race-sub">CPM $<input type="number" min="0" step="0.5" value={editForm.cpm_usd} onChange={(e) => setEditForm({ ...editForm, cpm_usd: e.target.value })} data-testid={`ad-camp-edit-cpm-${c.campaign_id}`} className="w-full bg-race-bg border border-race-line rounded-sm px-2 py-1 text-sm text-race-txt" /></label>
                    <label className="text-[10px] text-race-sub">Priority<input type="number" min="1" value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })} data-testid={`ad-camp-edit-priority-${c.campaign_id}`} className="w-full bg-race-bg border border-race-line rounded-sm px-2 py-1 text-sm text-race-txt" /></label>
                    <label className="text-[10px] text-race-sub">Budget $ (0 = none)<input type="number" min="0" value={editForm.budget_usd} onChange={(e) => setEditForm({ ...editForm, budget_usd: e.target.value })} data-testid={`ad-camp-edit-budget-${c.campaign_id}`} className="w-full bg-race-bg border border-race-line rounded-sm px-2 py-1 text-sm text-race-txt" /></label>
                    <label className="text-[10px] text-race-sub">Impr. cap (0 = none)<input type="number" min="0" value={editForm.impression_cap} onChange={(e) => setEditForm({ ...editForm, impression_cap: e.target.value })} data-testid={`ad-camp-edit-cap-${c.campaign_id}`} className="w-full bg-race-bg border border-race-line rounded-sm px-2 py-1 text-sm text-race-txt" /></label>
                    <label className="col-span-2 sm:col-span-4 text-[10px] text-race-sub">Landing URL (where clicks go)<input type="url" placeholder="https://sponsor.example.com" value={editForm.click_url} onChange={(e) => setEditForm({ ...editForm, click_url: e.target.value })} data-testid={`ad-camp-edit-clickurl-${c.campaign_id}`} className="w-full bg-race-bg border border-race-line rounded-sm px-2 py-1 text-sm text-race-txt" /></label>
                    <div className="col-span-2 sm:col-span-4 mt-1 pt-2 border-t border-race-line/60 space-y-3" data-testid={`ad-camp-edit-creatives-${c.campaign_id}`}>
                      {(editForm.variants || []).length >= 2 ? (
                        editForm.variants.map((v, vi) => (
                          <div key={v.id} className="space-y-2">
                            <div className="text-[10px] uppercase tracking-wide text-race-volt font-semibold">Variant {v.id} images</div>
                            {BANNER_SIZES.map((s) => (
                              <CreativeSlot key={s.key} size={s} value={v.creatives[s.key]} defaultPrompt={c.sponsor || c.name}
                                onChange={(url) => setEditForm((f) => {
                                  const variants = f.variants.map((x, i) => i === vi ? { ...x, creatives: { ...x.creatives, [s.key]: url } } : x);
                                  return { ...f, variants };
                                })} />
                            ))}
                          </div>
                        ))
                      ) : (
                        <>
                          <div className="text-[10px] uppercase tracking-wide text-race-sub font-semibold">Ad images (served live once saved)</div>
                          <GenAllSizes defaultPrompt={c.sponsor || c.name}
                            onResult={(key, url) => setEditForm((f) => ({ ...f, creatives: { ...f.creatives, [key]: url } }))} />
                          {BANNER_SIZES.map((s) => (
                            <CreativeSlot key={s.key} size={s} value={editForm.creatives[s.key]} defaultPrompt={c.sponsor || c.name}
                              onChange={(url) => setEditForm((f) => ({ ...f, creatives: { ...f.creatives, [s.key]: url } }))} />
                          ))}
                          <CreativeSlot size={FEATURE_SIZE} value={editForm.creatives.feature} defaultPrompt={c.sponsor || c.name}
                            onChange={(url) => setEditForm((f) => ({ ...f, creatives: { ...f.creatives, feature: url } }))} />
                  <FeatureAdPreview src={srcFor(editForm.creatives.feature)} className="py-2" />
                        </>
                      )}
                    </div>
                    <div className="col-span-2 sm:col-span-4 flex gap-2 mt-1">
                      <button onClick={() => saveEdit(c.campaign_id)} data-testid={`ad-camp-save-${c.campaign_id}`} className="bg-race-red text-race-bg font-semibold rounded-sm py-1.5 px-6 text-xs">Save</button>
                      <button onClick={() => setEditing(null)} data-testid={`ad-camp-cancel-${c.campaign_id}`} className="border border-race-line text-race-sub rounded-sm py-1.5 px-6 text-xs">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );})}
            {filteredCampaigns.length === 0 && <p className="text-sm text-race-sub" data-testid="ad-camp-empty">{campaigns.length === 0 ? "No campaigns yet." : "No campaigns match your filters."}</p>}
          </div>
        </div>
      </div>
      )}

      {subTab === "taxonomy" && (
        <TaxonomyManager taxonomy={taxonomy} campaigns={campaigns} reload={load} />
      )}
    </div>
    </AdminShell>
  );
}

function TaxonomyManager({ taxonomy, campaigns, reload }) {
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [newCatFirst, setNewCatFirst] = useState("");
  const [draftCats, setDraftCats] = useState([]);
  const [addingTo, setAddingTo] = useState(null);
  const [addVal, setAddVal] = useState("");
  const [menuFor, setMenuFor] = useState(null);

  const tax = (op, tag, extra = {}) => api.post("/ads/admin/taxonomy", { op, tag, ...extra });
  const usageOf = (tag) => campaigns.filter((c) => (c.target_tags || []).includes(tag)).length;

  // Merge persisted categories with session-draft (empty) categories.
  const merged = (() => {
    const map = new Map(taxonomy.map((c) => [c.category, [...c.tags].sort((a, b) => a.localeCompare(b))]));
    draftCats.forEach((c) => { if (!map.has(c)) map.set(c, []); });
    return Array.from(map, ([category, tags]) => ({ category, tags })).sort((a, b) => a.category.localeCompare(b.category));
  })();

  const ql = q.trim().toLowerCase();
  const visible = ql
    ? merged.map((c) => {
        const catMatch = c.category.toLowerCase().includes(ql);
        const tags = catMatch ? c.tags : c.tags.filter((t) => t.toLowerCase().includes(ql));
        return catMatch || tags.length ? { ...c, tags } : null;
      }).filter(Boolean)
    : merged;

  const addInterest = async (cat, name) => {
    const t = name.trim();
    if (!t) return;
    const exists = (merged.find((c) => c.category === cat)?.tags || []).some((x) => x.toLowerCase() === t.toLowerCase());
    if (exists) { toast.error(`"${t}" already exists in ${cat}.`); return; }
    try { await tax("add", t, { category: cat }); setDraftCats((d) => d.filter((c) => c !== cat)); setAddingTo(null); setAddVal(""); toast.success(`Added "${t}"`); reload(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail) || "Could not add interest."); }
  };
  const retireInterest = async (tag) => {
    const n = usageOf(tag);
    const msg = n > 0 ? `"${tag}" is currently used by ${n} campaign${n === 1 ? "" : "s"}. Deleting it will remove it from their ad personalization. Continue?` : `Delete "${tag}"?`;
    if (!window.confirm(msg)) return;
    try { await tax("retire", tag); toast.success(`Deleted "${tag}"`); reload(); } catch { toast.error("Delete failed"); }
  };
  const renameInterest = async (tag) => {
    const nt = window.prompt(`Rename interest "${tag}" to:`, tag);
    if (!nt || nt.trim() === tag) return;
    try { await tax("rename", tag, { new_tag: nt.trim() }); toast.success("Interest renamed"); reload(); } catch { toast.error("Rename failed"); }
  };
  const renameCategory = async (cat, tags) => {
    setMenuFor(null);
    const nc = window.prompt(`Rename category "${cat}" to:`, cat);
    if (!nc || nc.trim() === cat) return;
    try {
      if (tags.length === 0) setDraftCats((d) => d.map((c) => (c === cat ? nc.trim() : c)));
      else await Promise.all(tags.map((t) => tax("add", t, { category: nc.trim() })));
      toast.success("Category renamed"); reload();
    } catch { toast.error("Rename failed"); }
  };
  const deleteCategory = async (cat, tags) => {
    setMenuFor(null);
    if (tags.length === 0) { setDraftCats((d) => d.filter((c) => c !== cat)); return; }
    const total = tags.reduce((s, t) => s + usageOf(t), 0);
    const extra = total > 0 ? ` These interests are used by ${total} campaign reference${total === 1 ? "" : "s"}, which will be removed from ad personalization.` : "";
    if (!window.confirm(`Delete category "${cat}" and its ${tags.length} interest${tags.length === 1 ? "" : "s"}?${extra}`)) return;
    try { await Promise.all(tags.map((t) => tax("retire", t))); toast.success(`Deleted "${cat}"`); reload(); } catch { toast.error("Delete failed"); }
  };
  const createCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    if (merged.some((c) => c.category.toLowerCase() === name.toLowerCase())) { toast.error("That category already exists."); return; }
    if (newCatFirst.trim()) { try { await tax("add", newCatFirst.trim(), { category: name }); toast.success(`Category "${name}" created`); reload(); } catch { toast.error("Could not create category"); return; } }
    else { setDraftCats((d) => [...d, name]); toast.message(`"${name}" added`, { description: "Add its first interest to save it." }); }
    setNewCat(""); setNewCatFirst(""); setShowAdd(false);
  };

  return (
    <div className="max-w-[1200px]" data-testid="ad-taxonomy-mgmt">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-heading text-2xl tracking-tight">AD PERSONALIZATION TAXONOMY</h2>
          <p className="text-race-sub text-sm mt-1">Manage the interests advertisers can use to personalize campaigns.</p>
        </div>
        <button onClick={() => setShowAdd((v) => !v)} data-testid="ad-tax-add-category"
          className="shrink-0 flex items-center gap-2 bg-white hover:bg-race-txt text-race-bg font-semibold rounded-sm px-4 py-2 text-sm transition-colors"><Plus size={15} /> Add Category</button>
      </div>

      {showAdd && (
        <div className="bg-race-surface border border-race-line rounded-md p-4 mb-4" data-testid="ad-tax-add-panel">
          <div className="overline text-race-txt mb-2">Add Category</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="overline text-race-sub block mb-1">Category Name</label>
              <input autoFocus value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="e.g. Entertainment" data-testid="ad-tax-newcat-name"
                className="w-full bg-race-bg border border-race-line rounded-sm px-3 py-2 text-sm focus:border-race-red outline-none" />
            </div>
            <div>
              <label className="overline text-race-sub block mb-1">First interest <span className="text-race-muted normal-case tracking-normal text-[10px]">optional</span></label>
              <input value={newCatFirst} onChange={(e) => setNewCatFirst(e.target.value)} placeholder="Add an interest" data-testid="ad-tax-newcat-first"
                onKeyDown={(e) => e.key === "Enter" && createCategory()}
                className="w-full bg-race-bg border border-race-line rounded-sm px-3 py-2 text-sm focus:border-race-red outline-none" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setShowAdd(false); setNewCat(""); setNewCatFirst(""); }} className="text-race-sub hover:text-race-txt text-sm px-3 py-1.5">Cancel</button>
            <button onClick={createCategory} disabled={!newCat.trim()} data-testid="ad-tax-newcat-create"
              className="bg-white hover:bg-race-txt disabled:opacity-40 text-race-bg font-semibold rounded-sm px-4 py-1.5 text-sm transition-colors">Create Category</button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-race-sub pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search categories or interests…" data-testid="ad-tax-search"
            className="w-full bg-race-bg border border-race-line rounded-sm pl-9 pr-3 py-2 text-sm focus:border-race-red outline-none" />
        </div>
        <button onClick={() => setCollapsed({})} data-testid="ad-tax-expand-all" className="text-xs text-race-sub hover:text-race-txt border border-race-line rounded-sm px-3 py-2">Expand All</button>
        <button onClick={() => setCollapsed(Object.fromEntries(merged.map((c) => [c.category, true])))} data-testid="ad-tax-collapse-all" className="text-xs text-race-sub hover:text-race-txt border border-race-line rounded-sm px-3 py-2">Collapse All</button>
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="ad-tax-grid">
        {visible.map((c) => {
          const isCollapsed = !!collapsed[c.category];
          return (
            <div key={c.category} className="bg-race-surface border border-race-line rounded-lg p-4 flex flex-col" data-testid={`ad-tax-card-${c.category}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <button onClick={() => setCollapsed((s) => ({ ...s, [c.category]: !s[c.category] }))} className="text-left">
                  <div className="overline text-race-txt">{c.category}</div>
                  <div className="text-[11px] text-race-sub">{c.tags.length} interest{c.tags.length === 1 ? "" : "s"}</div>
                </button>
                <div className="relative">
                  <button onClick={() => setMenuFor(menuFor === c.category ? null : c.category)} data-testid={`ad-tax-menu-${c.category}`}
                    className="text-race-sub hover:text-race-txt px-1"><MoreHorizontal size={16} /></button>
                  {menuFor === c.category && (
                    <div className="absolute right-0 top-6 z-10 bg-race-bg border border-race-line rounded-md shadow-xl text-sm py-1 w-40" data-testid={`ad-tax-menu-panel-${c.category}`}>
                      <button onClick={() => renameCategory(c.category, c.tags)} className="w-full text-left px-3 py-2 hover:bg-race-surface text-race-txt">Rename Category</button>
                      <button onClick={() => deleteCategory(c.category, c.tags)} className="w-full text-left px-3 py-2 hover:bg-race-surface text-race-red">Delete Category</button>
                    </div>
                  )}
                </div>
              </div>
              {!isCollapsed && (
                <>
                  {c.tags.length === 0 ? (
                    <div className="text-[11px] text-race-sub mb-3">No interests yet.</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {c.tags.map((t) => (
                        <span key={t} className="group relative text-[11px] flex items-center gap-1 border border-race-line rounded-full pl-2.5 pr-1.5 py-1 text-race-txt" data-testid={`ad-tax-chip-${t}`}>
                          {t}
                          <span className="flex items-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                            <button onClick={() => renameInterest(t)} title="Rename" data-testid={`ad-tax-rename-${t}`} className="text-race-sub hover:text-race-txt px-0.5"><Pencil size={10} /></button>
                            <button onClick={() => retireInterest(t)} title="Delete" data-testid={`ad-tax-retire-${t}`} className="text-race-sub hover:text-race-red px-0.5"><X size={12} /></button>
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                  {addingTo === c.category ? (
                    <div className="flex items-center gap-2">
                      <input autoFocus value={addVal} onChange={(e) => setAddVal(e.target.value)} placeholder={`Add interest to ${c.category}`}
                        data-testid={`ad-tax-add-input-${c.category}`} onKeyDown={(e) => { if (e.key === "Enter") addInterest(c.category, addVal); if (e.key === "Escape") { setAddingTo(null); setAddVal(""); } }}
                        className="flex-1 bg-race-bg border border-race-line rounded-sm px-2 py-1.5 text-sm focus:border-race-red outline-none" />
                      <button onClick={() => addInterest(c.category, addVal)} className="bg-white text-race-bg rounded-sm px-3 py-1.5 text-xs font-semibold">Add</button>
                      <button onClick={() => { setAddingTo(null); setAddVal(""); }} className="text-race-sub hover:text-race-txt text-xs px-1">Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => { setAddingTo(c.category); setAddVal(""); }} data-testid={`ad-tax-add-interest-${c.category}`}
                      className="mt-auto self-start text-xs text-race-volt hover:underline flex items-center gap-1"><Plus size={12} /> {c.tags.length === 0 ? "Add First Interest" : "Add Interest"}</button>
                  )}
                </>
              )}
            </div>
          );
        })}
        {visible.length === 0 && <div className="text-race-sub text-sm col-span-full">No categories or interests match "{q}".</div>}
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-race-surface border border-race-line rounded-lg p-3">
    <div className="text-xl font-heading text-race-txt">{value}</div>
    <div className="text-[11px] text-race-sub uppercase tracking-wide">{label}</div>
  </div>
);
